import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { EramCpdlcDialog } from "@poscon/eram-cpdlc";
import { eramFontNameMap, eramTextDimensionMap } from "../../eramFont.js";
import { backgroundOption, borderOption, brightOption, counterOption, fontOption } from "../viewOptionFactory.js";
import { useViewOptions } from "../contexts/viewOptionContext.js";
import { cpdlcSessionsSelector, flightplansSelector, terminatedCpdlcSessionsSelector, useRootSelector, } from "../stateSelectors.js";
import { EramInput, useInputProps } from "./EramInput.js";
import { CpdlcMsgTable, getCpdlcMsgRowHeight } from "./CpdlcMsgTable.js";
import { Line } from "./Line.js";
import { colorNameMap } from "../../colorNameMap.js";
import { sectorIdSelector } from "../slices/connectionSlice.js";
import { computeColor } from "../../computeColor.js";
export const cpdlcHistView = "CPDLC_HIST";
const optionMap = {
    background: backgroundOption(cpdlcHistView),
    border: borderOption(cpdlcHistView),
    lines: counterOption(cpdlcHistView, "lines", "LINES", 2, 20, 8),
    font: fontOption(cpdlcHistView),
    bright: brightOption(cpdlcHistView),
};
export const initialCpdlcHistViewOptions = {
    background: true,
    border: true,
    lines: 12,
    font: 2,
    bright: 80,
};
/*
 * TI6110.100b - section 4.2.8.1
 * Message History View Format
 * The Message History View displays all **closed** CPDLC messages exchanged between
 * the local facility and the specified aircraft.
 */
export const CpdlcHistory = ({ ViewComponent }) => {
    const sectorId = useRootSelector(sectorIdSelector);
    const artccId = sectorId?.split(".")[0];
    const viewOptions = useViewOptions();
    const [searchStr, setSearchStr] = React.useState(null);
    const inputProps = useInputProps(`${cpdlcHistView}/input`, "", (s) => {
        setSearchStr(s);
    }, { initialShowInput: false, maxLength: 7 });
    const flightplans = useRootSelector(flightplansSelector);
    const selectedFp = searchStr
        ? Object.values(flightplans).find((fp) => fp.callsign === searchStr || fp.cid === searchStr)
        : null;
    const sessions = useRootSelector(cpdlcSessionsSelector);
    const terminatedSessions = useRootSelector(terminatedCpdlcSessionsSelector);
    const headerButtons = [
        {
            text: "DEL ALL",
            disabled: !selectedFp,
            onmousedown: () => {
                setSearchStr(null);
                inputProps.setInputValue("");
            },
        },
    ];
    const { font, bright, lines } = viewOptions;
    const alpha = bright / 100;
    const fontDim = eramTextDimensionMap[font];
    const tint = computeColor(colorNameMap.lightGrey, alpha);
    const dialogs = artccId
        ? Object.entries({ ...terminatedSessions, ...sessions }).reduce((acc, [fpId, session]) => {
            const fp = flightplans[fpId];
            if (fp && (!selectedFp || fp.callsign === selectedFp.callsign)) {
                for (const [, dialog] of Object.entries(session.dialogMap)) {
                    if (EramCpdlcDialog.dialogIsClosed(dialog) &&
                        ((!dialog.eligiblePosition && dialog.logicalDataAuthority === artccId) ||
                            dialog.eligiblePosition?.sectorId.startsWith(artccId))) {
                        acc.push({ fp, dialog });
                    }
                }
            }
            return acc;
        }, [])
        : [];
    const width = fontDim.width * 70;
    const rowHeight = getCpdlcMsgRowHeight(font);
    const rows = Math.min(lines, dialogs.length);
    const tableHeight = rowHeight * (rows + 1) + 3 * Math.floor(rows / 3);
    const height = eramTextDimensionMap[2].height + 11 + tableHeight;
    return (_jsxs(ViewComponent, { view: cpdlcHistView, optionMap: optionMap, width: 70, height: height, headerButtons: headerButtons, title: selectedFp ? `HISTORY ${selectedFp.callsign}` : "HISTORY", children: [_jsx("bitmapText", { x: 2, y: 5, text: "FLID", tint: tint, style: { fontFamily: eramFontNameMap[2] } }), _jsx(EramInput, { x: 4 + fontDim.width * 5, y: 4, width: fontDim.width * 8, borderTint: colorNameMap.mediumGrey, tint: tint, highlightColor: colorNameMap.white, ...inputProps }), _jsxs("container", { y: eramTextDimensionMap[2].height + 8, children: [_jsx(Line, { length: width - 1, tint: 0x303030 }), dialogs.length > 0 ? (_jsx(CpdlcMsgTable, { y: 1, maxRows: lines, totalWidth: width, fpDialogs: dialogs, view: cpdlcHistView, fontSize: font, alpha: alpha, noSort: true })) : (_jsx("bitmapText", { x: 2, y: 3, text: "NO CLOSED MESSAGES FOUND", tint: computeColor(colorNameMap.yellow, alpha), style: {
                            fontFamily: eramFontNameMap[2],
                        } }))] })] }));
};
//# sourceMappingURL=CpdlcHistory.js.map